import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";

// 使用 React.memo 包装组件，避免不必要的重新渲染
const CustomCarousel = React.memo(
  forwardRef(
    (
      {
        items = [],
        config,
        wrapperWidth,
        wrapperHeight,
        autoPlay = false,
        autoPlayInterval = 3000,
        onSlideChange,
        onClickActive = () => {},
      },
      ref,
    ) => {
      const [currentIndex, setCurrentIndex] = useState(0);
      const [isJumping, setIsJumping] = useState(false); // 标记是否为外部跳转
      const [isInstantJump, setIsInstantJump] = useState(false); // 标记是否为瞬间无动画跳转
      const prevIndexRef = useRef(0);
      const touchStartX = useRef(0);
      const touchStartY = useRef(0);
      const autoPlayTimerRef = useRef(null);
      const isMouseDown = useRef(false); // 标记鼠标是否按下

      // 1. 配置处理
      const { exit, visible = [], enter } = config;
      const N = Math.min(visible.length, 4);
      const originalLen = items.length;

      // 2. 补齐数据以支持无限循环逻辑 (确保至少有 N + 3 个节点)
      const displayItems = [...items];
      if (originalLen > 0 && originalLen < N + 3) {
        while (displayItems.length < N + 3) {
          displayItems.push(...items);
        }
      }
      const len = displayItems.length;

      // 3. 暴露给外部的 API
      useImperativeHandle(ref, () => ({
        // 获取当前激活帧在原始 items 中的索引
        getActiveIndex: () => {
          return currentIndex % originalLen;
        },
        // 跳转到指定索引
        setActiveIndex: (targetIndex) => {
          if (targetIndex < 0 || targetIndex >= originalLen) return;

          // 开启瞬间跳转标记，不使用动画
          setIsInstantJump(true);

          // 找到最近的物理索引进行跳转，避免大幅度跨度
          // 这里简单处理：直接映射到第一个循环周期
          setCurrentIndex(targetIndex);

          // 在下一个渲染周期后，关闭瞬间跳转标记，恢复动画
          setTimeout(() => {
            setIsInstantJump(false);
          }, 0);
        },
      }));

      // 监听 currentIndex 变化，触发 onSlideChange 事件
      useEffect(() => {
        // 计算原始索引
        const originalIndex = currentIndex % originalLen;

        // 只有当索引确实变化时才触发回调
        if (originalLen > 0) {
          onSlideChange?.(originalIndex);
        }
      }, [currentIndex, originalLen, onSlideChange]);

      useEffect(() => {
        prevIndexRef.current = currentIndex;
      }, [currentIndex]);

      // 自动切换逻辑
      useEffect(() => {
        if (autoPlay && items.length > 1) {
          // 清除之前的定时器
          if (autoPlayTimerRef.current) {
            clearInterval(autoPlayTimerRef.current);
          }

          // 设置新的定时器
          autoPlayTimerRef.current = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % len);
          }, autoPlayInterval);
        }

        // 清理函数
        return () => {
          if (autoPlayTimerRef.current) {
            clearInterval(autoPlayTimerRef.current);
          }
        };
      }, [autoPlay, autoPlayInterval, items.length, len]);

      // --- 手势处理 ---
      const handleTouchStart = (e) => {
        // 触摸开始时暂停自动播放
        if (autoPlayTimerRef.current) {
          clearInterval(autoPlayTimerRef.current);
        }
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
      };

      const handleTouchEnd = (e) => {
        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;
        const diffX = endX - touchStartX.current;
        if (Math.abs(diffX) > 40) {
          setIsJumping(false); // 手势滑动使用常规速度
          if (diffX > 0) {
            setCurrentIndex((prev) => (prev - 1 + len) % len);
          } else {
            setCurrentIndex((prev) => (prev + 1) % len);
          }
        }

        // 触摸结束后重启自动播放
        if (autoPlay && items.length > 1) {
          if (autoPlayTimerRef.current) {
            clearInterval(autoPlayTimerRef.current);
          }
          autoPlayTimerRef.current = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % len);
          }, autoPlayInterval);
        }
      };

      // --- PC端鼠标事件处理 ---
      const handleMouseDown = (e) => {
        // 鼠标按下时暂停自动播放
        console.log(e, "llllllllllll e value");
        if (autoPlayTimerRef.current) {
          clearInterval(autoPlayTimerRef.current);
        }
        touchStartX.current = e.clientX;
        touchStartY.current = e.clientY;
        isMouseDown.current = true;
      };

      const handleMouseMove = (e) => {
        // 只有鼠标按下时才处理
        if (!isMouseDown.current) return;
      };

      const handleMouseUp = (e) => {
        if (isMouseDown.current) {
          const endX = e.clientX;
          const endY = e.clientY;
          const diffX = endX - touchStartX.current;
          if (Math.abs(diffX) > 40) {
            setIsJumping(false); // 手势滑动使用常规速度
            if (diffX > 0) {
              setCurrentIndex((prev) => (prev - 1 + len) % len);
            } else {
              setCurrentIndex((prev) => (prev + 1) % len);
            }
          }
          isMouseDown.current = false;

          // 鼠标释放后重启自动播放
          if (autoPlay && items.length > 1) {
            if (autoPlayTimerRef.current) {
              clearInterval(autoPlayTimerRef.current);
            }
            autoPlayTimerRef.current = setInterval(() => {
              setCurrentIndex((prev) => (prev + 1) % len);
            }, autoPlayInterval);
          }
        }
      };

      const handleMouseLeave = (e) => {
        if (isMouseDown.current) {
          isMouseDown.current = false;

          // 鼠标离开后重启自动播放
          if (autoPlay && items.length > 1) {
            if (autoPlayTimerRef.current) {
              clearInterval(autoPlayTimerRef.current);
            }
            autoPlayTimerRef.current = setInterval(() => {
              setCurrentIndex((prev) => (prev + 1) % len);
            }, autoPlayInterval);
          }
        }
      };

      // --- 样式计算 ---
      const getStyle = (index) => {
        const oldDiff = (index - prevIndexRef.current + len) % len;
        const newDiff = (index - currentIndex + len) % len;

        // 动画开关逻辑
        let needsTransition = false;
        // 如果是瞬间跳转，不使用动画
        if (isInstantJump) {
          needsTransition = false;
        } else {
          // 1. 如果是跳转，只要在流水线内就允许动画
          // 2. 如果是滑动，仅允许相邻状态动画
          const isOldInView = oldDiff <= N || oldDiff === len - 1;
          const isNewInView = newDiff <= N || newDiff === len - 1;

          if (isOldInView && isNewInView) {
            if (isJumping) {
              needsTransition = true;
            } else {
              const normOld = oldDiff === len - 1 ? -1 : oldDiff;
              const normNew = newDiff === len - 1 ? -1 : newDiff;
              if (Math.abs(normOld - normNew) === 1) needsTransition = true;
            }
          }
        }

        // TODO 如果不想要动画直接将0.25s改成0
        const duration = isJumping ? "0.25s" : "0.5s"; // 跳转时动画更快

        const baseStyle = {
          position: "absolute",
          transformOrigin: "top left",
          transition: needsTransition
            ? `all ${duration} cubic-bezier(0.25, 0.1, 0.25, 1)`
            : "none",
          borderRadius: "12px",
          overflow: "hidden",
          backgroundColor: "#fff",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        };

        let targetConfig = null;
        if (newDiff === len - 1) {
          targetConfig = exit;
        } else if (newDiff < N) {
          targetConfig = visible[newDiff];
        } else if (newDiff === N) {
          targetConfig = enter;
        }

        if (targetConfig) {
          return {
            ...baseStyle,
            left: targetConfig.left,
            top: targetConfig.top,
            width: targetConfig.width || "240px",
            height: targetConfig.height || "200px",
            transform: `scale(${targetConfig.scale})`,
            opacity: targetConfig.opacity,
            zIndex: targetConfig.zIndex,
            visibility: "visible",
          };
        }

        return {
          ...baseStyle,
          left: "-9999px",
          opacity: 0,
          visibility: "hidden",
          transition: "none",
        };
      };

      return (
        <div
          className="carousel-container"
          style={{
            position: "relative",
            width: wrapperWidth,
            height: wrapperHeight,
            overflow: "hidden",
            cursor: "grab", // 鼠标悬停时显示抓取图标
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {displayItems.map((item, index) => (
            <div
              key={index}
              style={getStyle(index)}
              onClick={() => {
                if (currentIndex === index) {
                  onClickActive(item);
                }
              }}
            >
              <img
                src={item.imageUrl}
                onDragStart={(e) => e.preventDefault()}
                style={{ width: "100%", height: "100%", userSelect: "none" }}
              />
            </div>
          ))}
        </div>
      );
    },
  ),
);

// 添加比较函数，确保只有在props真正变化时才重新渲染
CustomCarousel.displayName = "CustomCarousel";

export default CustomCarousel;
